/**
 * Cookie readers for GA and Facebook pixel data.
 *
 * Shared between the Amplitude and Tracker plugins.
 * All functions are defensive — they return undefined on any parse error.
 */

function getCookie(name: string): string | undefined {
  try {
    const pairs = document.cookie.split(';');
    for (const pair of pairs) {
      const [key, ...rest] = pair.split('=');
      if (key?.trim() === name) {
        return rest.join('=').trim() || undefined;
      }
    }
  } catch {
    // SSR or cookie access error
  }
  return undefined;
}

/**
 * Extracts the GA client ID from the `_ga` cookie.
 * The cookie format is `GA1.1.<clientId>` where clientId is two dot-separated numbers.
 */
export function getClientId(): string | undefined {
  try {
    const pattern = /^GA\d\.\d\.(.+)$/;
    const raw = getCookie('_ga');
    if (!raw) return undefined;

    const match = raw.match(pattern);
    if (!match?.[1]) return undefined;

    const parts = match[1].split('.');
    if (parts.length >= 2) {
      return `${parts[0]}.${parts[1]}`;
    }
  } catch {
    // Ignore parse errors
  }
  return undefined;
}

/** Known prefixes in the GS2 cookie format. */
const gs2FormatPrefixes: Record<string, string> = {
  s: 'sessionId',
  o: 'sessionNumber',
  g: 'sessionEngaged',
  t: 'lastHitTimestamp',
  j: 'joinTimer',
  l: 'enhancedUserIdLoggedInState',
  h: 'enhancedUserId',
  d: 'joinId',
};

/**
 * Extracts session data from the `_ga_<MEASUREMENT_ID>` cookie.
 * Supports both GS1 and GS2 cookie formats.
 */
export function getSessionData(measurementId?: string): {
  sessionId?: string;
  sessionNumber?: string;
} {
  try {
    if (!measurementId) {
      // Try to find any _ga_ cookie as fallback
      const allCookies = document.cookie;
      const fallbackPattern = /_ga_[A-Z0-9]+=(GS\d)\.1\.([^;]+)/;
      const fallbackMatch = allCookies.match(fallbackPattern);
      if (!fallbackMatch) return {};

      return parseGaCookie(fallbackMatch[1], fallbackMatch[2]);
    }

    const [, mid] = measurementId.split('-');
    if (!mid) return {};

    const pattern = new RegExp(`_ga_${mid}=(GS\\d)\\.1\\.([^;]+)`);
    const match = document.cookie.match(pattern);
    if (!match) return {};

    return parseGaCookie(match[1], match[2]);
  } catch {
    // Ignore parse errors
  }
  return {};
}

function parseGaCookie(
  format: string | undefined,
  payload: string | undefined
): { sessionId?: string; sessionNumber?: string } {
  if (!format || !payload) return {};

  if (format === 'GS1') {
    const [sessionId, sessionNumber] = payload.split('.');
    if (sessionId && sessionNumber) {
      return { sessionId, sessionNumber };
    }
  }

  if (format === 'GS2') {
    const entries = payload.split('$').map((value) => {
      const [prefix, ...rest] = value;
      const key = (prefix && gs2FormatPrefixes[prefix]) || prefix;
      return [key, rest.join('')] as const;
    });
    const obj = Object.fromEntries(entries);
    return {
      sessionId: obj.sessionId,
      sessionNumber: obj.sessionNumber,
    };
  }

  return {};
}

/** Reads the Facebook pixel ID from the `_fbp` cookie. */
export function getFbpId(): string | undefined {
  return getCookie('_fbp');
}

/** Reads the Facebook click ID from the `_fbc` cookie. */
export function getFbcId(): string | undefined {
  return getCookie('_fbc');
}

/**
 * Reads cookie consent from the GTM dataLayer.
 * Returns the merged consent object, or undefined if unavailable.
 */
export function getCookieConsent(): Record<string, string> | undefined {
  try {
    if (typeof window === 'undefined' || !window.dataLayer) {
      return undefined;
    }

    const consentHistory = window.dataLayer.filter(
      (entry: unknown) => Array.isArray(entry) && entry[0] === 'consent'
    );

    if (consentHistory.length === 0) return undefined;

    return consentHistory.reduce<Record<string, string>>((acc, entry) => {
      const consentArr = entry as unknown[];
      const consentObj = consentArr?.[2];
      if (consentObj && typeof consentObj === 'object') {
        return { ...acc, ...(consentObj as Record<string, string>) };
      }
      return acc;
    }, {});
  } catch {
    // Ignore errors
  }
  return undefined;
}

/**
 * Aggregates all pixel/cookie data into a single object.
 * Used by the Amplitude plugin for identify calls.
 */
export function getPixels(
  measurementId?: string
): Record<string, string | Record<string, string> | undefined> {
  const { sessionId } = getSessionData(measurementId);
  const clientId = getClientId();
  const fbp = getFbpId();
  const fbc = getFbcId();
  const cookieConsent = getCookieConsent();

  if (!sessionId && !clientId && !fbp && !fbc) {
    return {};
  }

  return {
    ...(clientId && { clientId }),
    ...(sessionId && { sessionId }),
    ...(fbp && { fbp }),
    ...(fbc && { fbc }),
    cookieConsent: cookieConsent as Record<string, string> | undefined,
  };
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}
